import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { ClassDuesView } from '../lib/types';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { FamilyHomePage } from './FamilyHomePage';
import { CommandCenterPage } from './CommandCenterPage';

/** Roles that belong to a family, not to the school's staff. */
const FAMILY_ROLES = ['Parent', 'Student'];

/**
 * ⚠ The narrow roles land on THEIR page, not on the management overview.
 *
 * A Security user sent to the overview would hit the school-wide fee summary, get a 403, and
 * conclude the app is broken — the exact bug we already fixed for parents. A guard should log in
 * and see the gate. Nothing else.
 */
const HOME_FOR: Record<string, string> = {
  Security: '/gate',
  Warden: '/hostel',
  Librarian: '/library',
};

export function OverviewPage() {
  const { user } = useAuth();

  // A parent or student must NEVER be shown the school-wide fee summary — it's
  // management data, they get a 403, and they conclude the app is broken. They
  // get their own home screen instead. (This was a real bug, not a hypothetical.)
  if (user && FAMILY_ROLES.includes(user.role)) return <FamilyHomePage />;

  // A guard logs in and sees the gate. A warden sees the hostel. Same principle.
  if (user && HOME_FOR[user.role]) return <Navigate to={HOME_FOR[user.role]} replace />;

  // ⚠ ROLE-DERIVED FROM HERE DOWN.
  //
  // Previously every staff role fell through to StaffOverview, which fetches the school-wide fee
  // position. A Teacher logging in saw total outstanding, overdue rupees and the biggest debtors
  // by name. That is the accountant's screen.
  if (user && FEES_POSITION_ROLES.includes(user.role)) return <CommandCenterPage />;
  if (user && user.role === 'Teacher') return <TeacherOverview />;

  // Every other employed role (StoreKeeper, Receptionist, LabTechnician, SportsCoordinator,
  // TransportManager, HrManager): no fee data of any kind.
  return <PlainOverview />;
}

/**
 * Roles allowed to see the school's money.
 *
 * ⚠ Must stay in step with the "fees-position" policy in Program.cs. If they drift, the UI shows a
 * card that 403s — which is how the parent bug looked before it was fixed.
 */
const FEES_POSITION_ROLES = ['TrustAdmin', 'CampusAdmin', 'Accountant', 'Principal'];

/**
 * The class teacher's overview: which of MY students are behind on fees, by name.
 *
 * ⚠ No amounts, anywhere. See ClassDuesService on the server for the reasoning — the payload does
 * not contain a rupee figure, so this cannot leak one even if someone adds a field to the card.
 */
function TeacherOverview() {
  const { branding } = useTenant();
  const [view, setView] = useState<ClassDuesView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<ClassDuesView>('/api/ai/fees/my-class-dues')
      .then(setView)
      .catch(() => setView(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 6 }}>Overview</div>
      <h1 className="h-display" style={{ fontSize: 30, marginBottom: 4 }}>
        {branding?.name ?? 'Your institution'}
      </h1>
      <p style={{ color: 'var(--ink-2)', marginBottom: 28 }}>Your classes, at a glance.</p>

      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontWeight: 600 }}>Fees to follow up</span>
          {view?.scoped && view.overdueCount > 0 && (
            <span className="pill" style={{ marginLeft: 'auto', color: 'var(--danger)' }}>
              {view.overdueCount} overdue
            </span>
          )}
        </div>

        {loading && <p style={{ color: 'var(--ink-3)' }}>Checking your classes…</p>}

        {/* ⚠ Not scoped = this teacher has no timetable slot, so we cannot tell whose students
            they are. Show nothing and say why. Never fall back to the whole school. */}
        {!loading && view && !view.scoped && (
          <p style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.6 }}>
            You aren’t assigned to a class in the timetable yet, so there’s nothing to show here.
            The office can assign your classes.
          </p>
        )}

        {!loading && view?.scoped && view.students.length === 0 && (
          <p style={{ color: 'var(--ok)', fontSize: 14 }}>
            Everyone in your classes is up to date. Nothing to chase.
          </p>
        )}

        {!loading && view?.scoped && view.students.length > 0 && (
          <>
            <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
              Please ask these families to see the office. Amounts are with the accounts desk.
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {view.students.map((s) => (
                <div key={s.studentId} className="card" style={{
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--surface-2, transparent)',
                }}>
                  <div style={{ flex: 1 }}>
                    <strong>{s.fullName}</strong>{' '}
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {s.admissionNumber}
                    </span>
                    {s.classLabel && (
                      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {s.classLabel}</span>
                    )}
                  </div>
                  <span className="pill" style={{
                    fontSize: 11,
                    color: s.isOverdue ? 'var(--danger)' : 'var(--warn)',
                  }}>
                    {s.isOverdue ? 'Overdue' : 'Unpaid'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Every other employed role. No fee data at all — a storekeeper or a receptionist has no reason to
 * see who owes money, and the old behaviour showed them the entire school ledger.
 */
function PlainOverview() {
  const { branding } = useTenant();
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 6 }}>Overview</div>
      <h1 className="h-display" style={{ fontSize: 30, marginBottom: 4 }}>
        {branding?.name ?? 'Your institution'}
      </h1>
      <p style={{ color: 'var(--ink-2)', marginBottom: 28 }}>
        Use the menu to open your area of the school.
      </p>
    </div>
  );
}
